/**
 * Shared filter state and helpers for Dashboard and Properties.
 * Filters are combinable; default state = no filter.
 */

export type StatusFilter = 'online' | 'stale' | 'offline';
export type ProviderFilter = 'solark' | 'eg4' | 'franklinwh' | 'solaredge' | 'enphase' | 'tesla' | 'solis' | 'unknown';
/** All | Off-grid | Grid-primary. Legacy 'grid-tied'/'hybrid' treated as grid-primary. */
export type GridModeFilter = 'off-grid' | 'grid-primary' | 'grid-tied' | 'hybrid';

export interface FleetFilterState {
  status: StatusFilter | null;
  provider: ProviderFilter | null;
  gridMode: GridModeFilter | null;
  socMin: number | null;
  socMax: number | null;
  /** Show only systems with last update older than this many minutes (stale threshold) */
  lastUpdateStaleMinutes: number | null;
  /** Tag names (case-insensitive match) */
  tags: string[];
}

export const DEFAULT_FLEET_FILTER: FleetFilterState = {
  status: null,
  provider: null,
  gridMode: null,
  socMin: null,
  socMax: null,
  lastUpdateStaleMinutes: null,
  tags: [],
};

export type FleetSortKey = 'status' | 'soc' | 'lastUpdate' | 'name';

export const SAVED_VIEW_PAYLOAD_VERSION = 1;

export type PropertiesViewType = 'card' | 'list' | 'map';

export interface SavedViewPayload {
  version: number;
  filters: FleetFilterState;
  sortKey: FleetSortKey;
  viewType: PropertiesViewType;
}

/** Validate payload on load; return null if invalid so caller can fall back to default. */
export function parseSavedViewPayload(raw: unknown): SavedViewPayload | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (Number(o.version) !== SAVED_VIEW_PAYLOAD_VERSION) return null;
  const filters = o.filters;
  const sortKey = o.sortKey;
  const viewType = o.viewType;
  if (
    filters == null ||
    typeof filters !== 'object' ||
    typeof sortKey !== 'string' ||
    (viewType !== 'card' && viewType !== 'list' && viewType !== 'map')
  ) {
    return null;
  }
  const f = filters as Record<string, unknown>;
  if (
    !Array.isArray(f.tags) ||
    (f.status != null && typeof f.status !== 'string') ||
    (f.provider != null && typeof f.provider !== 'string') ||
    (f.gridMode != null && typeof f.gridMode !== 'string') ||
    (f.socMin != null && typeof f.socMin !== 'number') ||
    (f.socMax != null && typeof f.socMax !== 'number') ||
    (f.lastUpdateStaleMinutes != null && typeof f.lastUpdateStaleMinutes !== 'number')
  ) {
    return null;
  }
  return {
    version: SAVED_VIEW_PAYLOAD_VERSION,
    filters: filters as FleetFilterState,
    sortKey: sortKey as FleetSortKey,
    viewType,
  };
}

/** System-like item for filtering (fleet card or property row) */
export interface FilterableSystem {
  id: string;
  online: boolean;
  dataStale?: boolean;
  provider: string;
  socPct: number;
  lastPollAt: string | null;
  tags?: string[];
  /** Grid state when available; absent = N/A */
  gridState?: 'import' | 'export' | 'off-grid' | null;
}

function statusMatches(filter: StatusFilter | null, item: FilterableSystem): boolean {
  if (!filter) return true;
  if (filter === 'online') return item.online === true;
  if (filter === 'stale') return Boolean(item.dataStale);
  if (filter === 'offline') return item.online === false;
  return true;
}

function providerMatches(filter: ProviderFilter | null, item: FilterableSystem): boolean {
  if (!filter) return true;
  return item.provider === filter;
}

function gridModeMatches(filter: GridModeFilter | null, item: FilterableSystem): boolean {
  if (!filter) return true;
  const mode = item.propertyGridMode;
  // NULL = treat as off_grid; include in off-grid filter, exclude from grid-primary
  if (filter === 'off-grid') return mode === 'off_grid' || mode == null;
  if (filter === 'grid-primary' || filter === 'grid-tied' || filter === 'hybrid') return mode === 'grid_primary';
  return true;
}

function socMatches(socMin: number | null, socMax: number | null, item: FilterableSystem): boolean {
  if (socMin != null && item.socPct < socMin) return false;
  if (socMax != null && item.socPct > socMax) return false;
  return true;
}

function lastUpdateMatches(staleMinutes: number | null, item: FilterableSystem): boolean {
  if (staleMinutes == null) return true;
  if (!item.lastPollAt) return true; // never polled = include
  const diff = (Date.now() - new Date(item.lastPollAt).getTime()) / (1000 * 60);
  return diff >= staleMinutes;
}

function tagsMatch(filterTags: string[], item: FilterableSystem): boolean {
  if (filterTags.length === 0) return true;
  const itemTags = (item.tags ?? []).map((t) => t.toLowerCase());
  return filterTags.every((t) => itemTags.includes(t.toLowerCase()));
}

export function applyFleetFilters<T extends FilterableSystem>(
  items: T[],
  state: FleetFilterState
): T[] {
  return items.filter(
    (item) =>
      statusMatches(state.status, item) &&
      providerMatches(state.provider, item) &&
      gridModeMatches(state.gridMode, item) &&
      socMatches(state.socMin, state.socMax, item) &&
      lastUpdateMatches(state.lastUpdateStaleMinutes, item) &&
      tagsMatch(state.tags, item)
  );
}

export function sortFleetSystems<T extends FilterableSystem>(
  items: T[],
  sortKey: FleetSortKey,
  getName: (item: T) => string
): T[] {
  const sorted = [...items].sort((a, b) => {
    switch (sortKey) {
      case 'status': {
        const score = (x: FilterableSystem) =>
          !x.online ? 0 : x.dataStale ? 1 : 2;
        return score(a) - score(b);
      }
      case 'soc':
        return b.socPct - a.socPct;
      case 'lastUpdate': {
        const at = (x: FilterableSystem) =>
          x.lastPollAt ? new Date(x.lastPollAt).getTime() : 0;
        return at(b) - at(a);
      }
      case 'name':
        return getName(a).localeCompare(getName(b));
      default:
        return 0;
    }
  });
  return sorted;
}

export function hasActiveFilters(state: FleetFilterState): boolean {
  return (
    state.status != null ||
    state.provider != null ||
    state.gridMode != null ||
    state.socMin != null ||
    state.socMax != null ||
    state.lastUpdateStaleMinutes != null ||
    state.tags.length > 0
  );
}
